import { useTenantFeatureConfigValue } from '@repo/crm-atoms';
import { TenantFeatureConfigurationFeatureName } from '@repo/utils';
import {
  Flex, InputNumber, Select, Typography,
} from 'antd';
import {
  AUTOMATION_EXECUTION_TIME,
  MAX_BUFFER_MINUTES_CONFIG_KEY,
  maxValueForUnit,
  timeBufferDirectionOptions,
  TimeBufferUnit,
  timeBufferUnitOptions,
} from 'automations/data/timeBuffer';
import useMailMergeFields from 'automations/hooks/useMailMergeFields';
import { useCallback, useMemo } from 'react';

import styles from './TimeBufferInput.module.scss';

export interface TimeBufferValue {
  value?: number | null;
  unit?: TimeBufferUnit;
  isAfter?: boolean;
  dependsOn?: string;
}

export const emptyTimeBuffer: TimeBufferValue = {
  value: 1,
  unit: TimeBufferUnit.HOURS,
  isAfter: true,
};

export default function TimeBufferInput({
  value,
  onChange,
  disabled = false,
  showAnchor = true,
  anchorPlaceholder = 'Anchor to',
  label,
}: {
  value?: TimeBufferValue;
  onChange?: (value: TimeBufferValue) => void;
  disabled?: boolean;
  showAnchor?: boolean;
  anchorPlaceholder?: string;
  label?: string;
}) {
  const current = value ?? emptyTimeBuffer;

  const maxBufferMinutes = useTenantFeatureConfigValue(
    TenantFeatureConfigurationFeatureName.AutomationTimeBuffer,
    MAX_BUFFER_MINUTES_CONFIG_KEY,
  ) as number;

  // Unfiltered by design: every lead and activity attribute is offered as an anchor.
  //
  // NOTE: the executor only resolves a datetime anchor — AutomationExecutors::Base
  // #activity_attribute_value returns nil for any other data_type, and TimeResolver refuses
  // date-only values because midnight would be invented by the parse. Anchoring to a
  // non-datetime attribute therefore skips the call at runtime with "the date & time it is
  // bound to is not set on the lead". Listing them is a deliberate product choice; the form
  // does not warn.
  const { options: mailMergeOptions } = useMailMergeFields({});

  const anchorOptions = useMemo(() => [
    { label: AUTOMATION_EXECUTION_TIME, value: AUTOMATION_EXECUTION_TIME },
    ...mailMergeOptions,
  ], [mailMergeOptions]);

  const maxValue = maxValueForUnit(current.unit ?? TimeBufferUnit.HOURS, maxBufferMinutes);

  const update = useCallback((patch: Partial<TimeBufferValue>) => {
    onChange?.({ ...current, ...patch });
  }, [current, onChange]);

  const handleUnitChange = useCallback((unit: TimeBufferUnit) => {
    const ceiling = maxValueForUnit(unit, maxBufferMinutes);
    const clamped = ceiling && (current.value ?? 0) > ceiling ? ceiling : current.value;
    update({ unit, value: clamped });
  }, [current.value, maxBufferMinutes, update]);

  return (
    <Flex gap={8} align="center" wrap="wrap">
      {label && <Typography.Text>{label}</Typography.Text>}
      <InputNumber
        min={1}
        max={maxValue}
        value={current.value}
        onChange={(next) => update({ value: next })}
        disabled={disabled}
        className={styles.amount}
      />
      <Select
        options={timeBufferUnitOptions}
        value={current.unit}
        onChange={handleUnitChange}
        disabled={disabled}
        className={styles.unit}
      />
      <Select
        options={timeBufferDirectionOptions}
        value={current.isAfter}
        onChange={(isAfter) => update({ isAfter })}
        disabled={disabled}
        className={styles.direction}
      />
      {showAnchor && (
        <Select
          options={anchorOptions}
          value={current.dependsOn}
          onChange={(dependsOn) => update({ dependsOn })}
          disabled={disabled}
          placeholder={anchorPlaceholder}
          className={styles.anchor}
        />
      )}
      {maxValue !== undefined && (
        <Typography.Text type="secondary" className={styles.hint}>
          {`max ${maxValue}`}
        </Typography.Text>
      )}
    </Flex>
  );
}
